import { z } from 'zod';
import { isBlankChild, type ReferralPayloadInput } from './dto/my-referrals';

export const SCHOOL_NAME = 'Gulliver Prep';

export const REFERRAL_STEPS = [
  { step: 1, id: 'trustee', label: 'Trustee' },
  { step: 2, id: 'family', label: 'Family' },
  { step: 3, id: 'applicants', label: 'Applicants' },
  { step: 4, id: 'referral', label: 'Referral' },
  { step: 5, id: 'review', label: 'Review' },
] as const;
export type ReferralStepId = (typeof REFERRAL_STEPS)[number]['id'];

export interface ReferralCopyField {
  key: string;
  name: string;
  text: string;
  long?: boolean;
}

export interface ReferralCopySection {
  step: ReferralStepId;
  fields: ReferralCopyField[];
}

export const REFERRAL_FORM_COPY: readonly ReferralCopySection[] = [
  {
    step: 'trustee',
    fields: [
      { key: 'step1_title', name: 'Step heading', text: 'Trustee information' },
      {
        key: 'step1_hint',
        name: 'Step help text',
        text: 'Taken from your HV account and not editable here.',
        long: true,
      },
      { key: 'trustee_name', name: 'Name label', text: 'Trustee name' },
      { key: 'trustee_email', name: 'Email label', text: 'Trustee email' },
    ],
  },
  {
    step: 'family',
    fields: [
      { key: 'step2_title', name: 'Step heading', text: 'Family information' },
      {
        key: 'family_last_name',
        name: 'Family last name label',
        text: 'Referred family last name',
      },
      { key: 'cycle', name: 'Academic year label', text: 'Applying academic year' },
      { key: 'guardian_legend', name: 'Parent/guardian group heading', text: 'Parent/guardian' },
      { key: 'guardian_optional_suffix', name: 'Second parent suffix', text: '(optional)' },
      { key: 'guardian_first_name', name: 'Parent first name label', text: 'First name' },
      { key: 'guardian_last_name', name: 'Parent last name label', text: 'Last name' },
      { key: 'guardian_email', name: 'Parent email label', text: 'Email' },
      { key: 'guardian_phone', name: 'Parent phone label', text: 'Phone (optional)' },
      {
        key: 'relationship',
        name: 'Relationship question',
        text: 'Your relationship or connection to the family',
        long: true,
      },
    ],
  },
  {
    step: 'applicants',
    fields: [
      { key: 'step3_title', name: 'Step heading', text: 'Applicants' },
      {
        key: 'step3_hint',
        name: 'Step help text',
        text: 'Add each child you are referring. Dates of birth are not collected.',
        long: true,
      },
      { key: 'add_child', name: 'Add child button', text: 'Add another child' },
      { key: 'child_legend', name: 'Child group heading', text: 'Child' },
      { key: 'legal_first_name', name: 'Legal first name label', text: 'Legal first name' },
      {
        key: 'preferred_name',
        name: 'Preferred name label',
        text: 'Preferred first name (optional)',
      },
      { key: 'middle_name', name: 'Middle name label', text: 'Middle name (optional)' },
      { key: 'applicant_last_name', name: 'Last name label', text: 'Last name' },
      { key: 'applying_grade', name: 'Applying grade label', text: 'Applying grade' },
      { key: 'current_school', name: 'Current school label', text: 'Current school (optional)' },
      { key: 'city', name: 'City label', text: 'City (optional)' },
      {
        key: 'state_or_country',
        name: 'State or country label',
        text: 'State or country (optional)',
      },
    ],
  },
  {
    step: 'referral',
    fields: [
      { key: 'step4_title', name: 'Step heading', text: 'Referral' },
      {
        key: 'connection_narrative',
        name: 'Connection question',
        text: 'How are you connected to this family?',
        long: true,
      },
      {
        key: 'community_contribution',
        name: 'Community contribution question',
        text: 'What would you like Admissions to understand about this family and its potential contribution to the {school} community?',
        long: true,
      },
      {
        key: 'academic_familiarity',
        name: 'Academic standing question',
        text: "Are you familiar with this applicant's academic standing at the current school?",
        long: true,
      },
      {
        key: 'academic_context',
        name: 'Academic context label',
        text: 'Relevant academic context',
      },
      {
        key: 'disciplinary_familiarity',
        name: 'Disciplinary standing question',
        text: "Are you familiar with this applicant's disciplinary standing at the current school?",
        long: true,
      },
      {
        key: 'disciplinary_context',
        name: 'Disciplinary context label',
        text: 'Relevant disciplinary context',
      },
      {
        key: 'first_choice',
        name: 'First-choice question',
        text: "Is {school} currently the family's first-choice school?",
        long: true,
      },
      {
        key: 'enrollment_interest',
        name: 'Enrollment-interest label',
        text: 'Additional enrollment-interest context (optional)',
      },
      {
        key: 'additional_information',
        name: 'Additional information label',
        text: 'Additional information (optional)',
      },
    ],
  },
  {
    step: 'review',
    fields: [
      { key: 'step5_title', name: 'Step heading', text: 'Review' },
      {
        key: 'step5_hint',
        name: 'Step help text',
        text: 'Check the details below. Use Back to change anything.',
        long: true,
      },
      {
        key: 'allotment_question',
        name: 'Trustee recommendation question',
        text: 'Is this one of your ({limit}) allotted Trustee recommendations?',
        long: true,
      },
      {
        key: 'allotment_checkbox',
        name: 'Trustee recommendation checkbox label',
        text: 'Yes, this is one of my ({limit}) allotted Trustee recommendations',
        long: true,
      },
      {
        key: 'acknowledgement',
        name: 'Acknowledgement',
        text: 'I understand that this referral provides context to the Office of Admission and does not guarantee admission, alter admission requirements, or determine an admission decision. The information I have provided is accurate to the best of my knowledge and should be treated as confidential.',
        long: true,
      },
    ],
  },
];

export const REFERRAL_COPY_DEFAULTS: Readonly<Record<string, string>> = Object.fromEntries(
  REFERRAL_FORM_COPY.flatMap((s) => s.fields.map((f) => [f.key, f.text])),
);

export type ReferralCopyVars = Readonly<Record<string, string | number>>;

export function referralCopy(overrides: Readonly<Record<string, string>> = {}) {
  return (key: string, vars: ReferralCopyVars = {}): string =>
    Object.entries({ school: SCHOOL_NAME, ...vars }).reduce(
      (text, [name, value]) => text.replaceAll(`{${name}}`, String(value)),
      overrides[key] ?? REFERRAL_COPY_DEFAULTS[key] ?? key,
    );
}

export const REFERRAL_FORM_BLOCKS = {
  family: ['family_last_name', 'cycle', 'guardians', 'relationship'],
  applicants: [
    'legal_first_name',
    'preferred_name',
    'middle_name',
    'applicant_last_name',
    'applying_grade',
    'current_school',
    'city',
    'state_or_country',
  ],
  referral: [
    'connection_narrative',
    'community_contribution',
    'screening',
    'first_choice',
    'enrollment_interest',
    'additional_information',
  ],
} as const satisfies Partial<Record<ReferralStepId, readonly string[]>>;
export type ReferralBlockStep = keyof typeof REFERRAL_FORM_BLOCKS;
export type ReferralBlockId<S extends ReferralBlockStep> = (typeof REFERRAL_FORM_BLOCKS)[S][number];

export const REFERRAL_BLOCK_NAMES: Record<ReferralBlockId<ReferralBlockStep>, string> = {
  family_last_name: 'Family last name',
  cycle: 'Applying academic year',
  guardians: 'Parents/guardians',
  relationship: 'Relationship to the family',
  legal_first_name: 'Legal first name',
  preferred_name: 'Preferred first name',
  middle_name: 'Middle name',
  applicant_last_name: 'Last name',
  applying_grade: 'Applying grade',
  current_school: 'Current school',
  city: 'City',
  state_or_country: 'State or country',
  connection_narrative: 'Connection question',
  community_contribution: 'Community contribution question',
  screening: 'Per-child screening questions',
  first_choice: 'First-choice question',
  enrollment_interest: 'Enrollment-interest context',
  additional_information: 'Additional information',
};

export const MAX_REFERRAL_COPY_LENGTH = 600;

export const HIDEABLE_REFERRAL_QUESTIONS = ['academic_standing', 'disciplinary_standing'] as const;
export const HideableReferralQuestion = z.enum(HIDEABLE_REFERRAL_QUESTIONS);
export type HideableReferralQuestion = z.infer<typeof HideableReferralQuestion>;
export const HIDEABLE_REFERRAL_QUESTION_NAMES: Record<HideableReferralQuestion, string> = {
  academic_standing: 'Academic standing (per child)',
  disciplinary_standing: 'Disciplinary standing (per child)',
};

export type ReferralFormOrder = { [S in ReferralBlockStep]: ReferralBlockId<S>[] };
const BLOCK_STEPS = Object.keys(REFERRAL_FORM_BLOCKS) as ReferralBlockStep[];

export function defaultReferralOrder(): ReferralFormOrder {
  return {
    family: [...REFERRAL_FORM_BLOCKS.family],
    applicants: [...REFERRAL_FORM_BLOCKS.applicants],
    referral: [...REFERRAL_FORM_BLOCKS.referral],
  };
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

export function sanitizeReferralCopy(input: unknown): Record<string, string> {
  const source = isRecord(input) ? input : {};
  const out: Record<string, string> = {};
  for (const [key, fallback] of Object.entries(REFERRAL_COPY_DEFAULTS)) {
    const value = source[key];
    if (typeof value !== 'string') continue;
    const text = value.trim().slice(0, MAX_REFERRAL_COPY_LENGTH);
    if (text && text !== fallback) out[key] = text;
  }
  return out;
}

export function sanitizeReferralOrder(input: unknown): ReferralFormOrder {
  const source = isRecord(input) ? input : {};
  const out = defaultReferralOrder();
  for (const step of BLOCK_STEPS) {
    const known: readonly string[] = REFERRAL_FORM_BLOCKS[step];
    const requested = Array.isArray(source[step]) ? (source[step] as unknown[]) : [];
    const ordered: string[] = [];
    for (const id of requested)
      if (typeof id === 'string' && known.includes(id) && !ordered.includes(id)) ordered.push(id);
    for (const id of known) if (!ordered.includes(id)) ordered.push(id);
    (out as Record<ReferralBlockStep, string[]>)[step] = ordered;
  }
  return out;
}

export function sanitizeHiddenQuestions(input: unknown): HideableReferralQuestion[] {
  const values = Array.isArray(input) ? input : [];
  return HIDEABLE_REFERRAL_QUESTIONS.filter((q) => values.includes(q));
}

export const ReferralFormCopy = z
  .record(
    z.string(),
    z
      .string()
      .trim()
      .max(MAX_REFERRAL_COPY_LENGTH, `At most ${MAX_REFERRAL_COPY_LENGTH} characters`),
  )
  .transform(sanitizeReferralCopy);

export const ReferralFormOrderInput = z
  .record(z.string(), z.array(z.string().max(64)).max(50))
  .transform(sanitizeReferralOrder);

export const HiddenReferralQuestions = z
  .array(HideableReferralQuestion)
  .max(HIDEABLE_REFERRAL_QUESTIONS.length * 2)
  .transform(sanitizeHiddenQuestions);

export function hideReferralAnswers<P extends Pick<ReferralPayloadInput, 'children'>>(
  payload: P,
  hidden: readonly HideableReferralQuestion[],
): P {
  if (!hidden.length) return payload;
  const academic = hidden.includes('academic_standing');
  const disciplinary = hidden.includes('disciplinary_standing');
  return {
    ...payload,
    children: payload.children.map((c) => ({
      ...c,
      ...(academic && { academicFamiliarity: 'unknown' as const, academicContext: '' }),
      ...(disciplinary && { disciplinaryFamiliarity: 'unknown' as const, disciplinaryContext: '' }),
    })),
  };
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export interface ReferralDraftValues {
  cycleId: string | null;
  payload: ReferralPayloadInput;
}

export interface ReferralValidationContext {
  grades: readonly string[];
}

export function referralStepIssues(
  draft: ReferralDraftValues,
  step: number,
  ctx: ReferralValidationContext,
): string[] {
  const p = draft.payload;
  const issues: string[] = [];
  if (step === 2) {
    if (!p.familyLastName.trim()) issues.push('Family last name is required.');
    if (!draft.cycleId) issues.push('Choose the applying academic year.');
    const g1 = p.guardians[0];
    if (!g1?.firstName.trim()) issues.push('Parent/guardian 1: first name is required.');
    if (!g1?.lastName.trim()) issues.push('Parent/guardian 1: last name is required.');
    if (!g1?.email.trim()) issues.push('Parent/guardian 1: email is required.');
    p.guardians.forEach((g, i) => {
      if (g.email.trim() && !EMAIL.test(g.email.trim()))
        issues.push(`Parent/guardian ${i + 1}: email is not a valid email address.`);
    });
    if (!p.relationship.trim())
      issues.push('Your relationship or connection to the family is required.');
  }
  if (step === 3) {
    const children = p.children.filter((c) => !isBlankChild(c));
    if (!children.length) issues.push('Add at least one child.');
    children.forEach((c, i) => {
      const who = `Child ${i + 1}`;
      if (!c.legalFirstName.trim()) issues.push(`${who}: legal first name is required.`);
      if (!c.lastName.trim()) issues.push(`${who}: last name is required.`);
      if (!c.grade) issues.push(`${who}: choose the applying grade.`);
      else if (!ctx.grades.includes(c.grade))
        issues.push(
          `${who}: the grade "${c.grade}" is no longer offered; choose the applying grade again.`,
        );
    });
  }
  if (step === 4) {
    if (!p.connectionNarrative.trim())
      issues.push('Describe how you are connected to this family.');
    if (!p.communityContribution.trim())
      issues.push('Describe what Admissions should understand about this family.');
  }
  return issues;
}

export function referralIssuesBefore(
  draft: ReferralDraftValues,
  target: number,
  ctx: ReferralValidationContext,
): string[] {
  const issues: string[] = [];
  for (let step = 1; step < target; step++) issues.push(...referralStepIssues(draft, step, ctx));
  return issues;
}

export interface ReferralSubmitContext extends ReferralValidationContext {
  cycleOpen: boolean;
  acknowledged: boolean;
}

export function referralSubmitIssues(draft: ReferralDraftValues, ctx: ReferralSubmitContext): string[] {
  const issues = referralIssuesBefore(draft, REFERRAL_STEPS.length, ctx);
  if (draft.cycleId && !ctx.cycleOpen)
    issues.push('The applying academic year is no longer open to trustee referrals. Choose another year on the Family step.');
  if (!ctx.acknowledged) issues.push('Tick the acknowledgement to submit.');
  return issues;
}

export function referralEditIssues(draft: ReferralDraftValues, ctx: ReferralValidationContext): string[] {
  return referralIssuesBefore(draft, REFERRAL_STEPS.length, ctx);
}
